import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Tabs,
  Tab,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Storage as StorageIcon,
  PlayArrow as TestIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as ConnectedIcon,
  Error as ErrorIcon,
  Schedule as UntestedIcon
} from '@mui/icons-material';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErrorMessage from '../../components/common/ErrorMessage';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const DatabaseToolsPage = () => {
  const [tabValue, setTabValue] = useState(0);
  const [connections, setConnections] = useState([]);
  const [knowledgeBases, setKnowledgeBases] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  
  // 연결 생성 대화상자
  const [openConnectionDialog, setOpenConnectionDialog] = useState(false);
  const [newConnection, setNewConnection] = useState({
    connection_name: '',
    db_type: 'mysql',
    host: '',
    port: 3306,
    database: '',
    username: '',
    password: '',
    description: '',
    target_knowledge_bases: []
  });

  // 스키마 정보
  const [schemaData, setSchemaData] = useState({});
  const [selectedConnection, setSelectedConnection] = useState(null);
  
  // 쿼리 테스트
  const [testQuery, setTestQuery] = useState('');
  const [queryResult, setQueryResult] = useState(null);

  useEffect(() => {
    fetchConnections();
    fetchKnowledgeBases();
  }, []);

  const fetchConnections = async () => {
    try {
      setIsLoading(true);
      const response = await fetch('/api/db-tools/connections');
      if (!response.ok) throw new Error('연결 목록 조회 실패');
      
      const data = await response.json();
      setConnections(data);
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchKnowledgeBases = async () => {
    try {
      const response = await fetch('/api/db-tools/knowledge-bases');
      if (!response.ok) throw new Error('지식베이스 목록 조회 실패');
      
      const data = await response.json();
      setKnowledgeBases(data);
    } catch (error) {
      console.error('지식베이스 목록 조회 실패:', error);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleCreateConnection = async () => {
    try {
      setIsLoading(true);
      
      const response = await fetch('/api/db-tools/connections', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newConnection)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'DB 연결 생성 실패');
      }

      const result = await response.json();
      setSuccessMessage(result.message);
      setOpenConnectionDialog(false);
      
      // 폼 초기화
      setNewConnection({
        connection_name: '',
        db_type: 'mysql',
        host: '',
        port: 3306,
        database: '',
        username: '',
        password: '',
        description: '',
        target_knowledge_bases: []
      });

      // 목록 새로고침
      fetchConnections();
      
      setTimeout(() => setSuccessMessage(null), 3000);

    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTestConnection = async (connectionName) => {
    try {
      setIsLoading(true);
      
      const response = await fetch(`/api/db-tools/connections/${connectionName}/test`);
      if (!response.ok) throw new Error('연결 테스트 실패');
      
      const result = await response.json();
      
      if (result.success) {
        setSuccessMessage('연결 테스트 성공!');
      } else {
        setError(`연결 테스트 실패: ${result.error}`);
      }
      
      // 목록 새로고침
      fetchConnections();
      
      setTimeout(() => {
        setSuccessMessage(null);
        setError(null);
      }, 3000);

    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDiscoverSchema = async (connectionName) => {
    try {
      setIsLoading(true);
      
      const response = await fetch(`/api/db-tools/connections/${connectionName}/discover-schema`, {
        method: 'POST'
      });
      
      if (!response.ok) throw new Error('스키마 발견 실패');
      
      const result = await response.json();
      setSchemaData(prev => ({
        ...prev,
        [connectionName]: result
      }));
      
      setSuccessMessage(`스키마 발견 완료: ${result.total_tables}개 테이블, ${result.total_columns}개 컬럼`);
      setTimeout(() => setSuccessMessage(null), 3000);

    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteConnection = async (connectionName) => {
    if (!window.confirm(`'${connectionName}' 연결을 삭제하시겠습니까?`)) {
      return;
    }

    try {
      setIsLoading(true);
      
      const response = await fetch(`/api/db-tools/connections/${connectionName}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) throw new Error('연결 삭제 실패');
      
      const result = await response.json();
      setSuccessMessage(result.message);
      
      // 목록 새로고침
      fetchConnections();
      
      setTimeout(() => setSuccessMessage(null), 3000);

    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'connected':
        return <ConnectedIcon color="success" />;
      case 'error':
        return <ErrorIcon color="error" />;
      default:
        return <UntestedIcon color="action" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'connected':
        return 'success';
      case 'error':
        return 'error';
      default:
        return 'default';
    }
  };

  const handleTestQuery = async () => {
    if (!selectedConnection || !testQuery) return;
    
    try {
      setIsLoading(true);
      setQueryResult(null);
      
      const response = await fetch(`/api/db-tools/connections/${selectedConnection}/test-query`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          natural_question: testQuery
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || '쿼리 테스트 실패');
      }

      const result = await response.json();
      setQueryResult(result);
      
      if (result.success) {
        setSuccessMessage('쿼리가 성공적으로 실행되었습니다!');
      } else {
        setError(`쿼리 실행 실패: ${result.error_message}`);
      }
      
      setTimeout(() => {
        setSuccessMessage(null);
        setError(null);
      }, 3000);

    } catch (error) {
      setError(error.message);
      setQueryResult({
        success: false,
        error_message: error.message,
        question: testQuery,
        generated_sql: '',
        execution_result: [],
        row_count: 0,
        execution_time_ms: 0
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        데이터베이스 도구 관리
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        외부 데이터베이스와 연결하여 실시간 쿼리를 수행할 수 있는 도구를 관리합니다.
      </Typography>

      {/* 성공/오류 메시지 */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 3 }}>
        <Tab label="연결 관리" />
        <Tab label="스키마 설정" />
        <Tab label="비즈니스 룰" />
        <Tab label="테스트" />
      </Tabs>

      {/* 연결 관리 탭 */}
      <TabPanel value={tabValue} index={0}>
        <Card>
          <CardHeader 
            title="데이터베이스 연결"
            action={
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setOpenConnectionDialog(true)}
              >
                연결 추가
              </Button>
            }
          />
          <CardContent>
            {isLoading ? (
              <LoadingSpinner message="연결 목록을 불러오는 중..." />
            ) : connections.length === 0 ? (
              <Alert severity="info">
                등록된 데이터베이스 연결이 없습니다. 새 연결을 추가해주세요.
              </Alert>
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>상태</TableCell>
                      <TableCell>연결 이름</TableCell>
                      <TableCell>타입</TableCell>
                      <TableCell>호스트</TableCell>
                      <TableCell>데이터베이스</TableCell>
                      <TableCell>지식베이스</TableCell>
                      <TableCell>마지막 테스트</TableCell>
                      <TableCell>작업</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {connections.map((connection) => (
                      <TableRow key={connection.connection_name}>
                        <TableCell>
                          <Tooltip title={connection.status}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              {getStatusIcon(connection.status)}
                              <Chip 
                                label={connection.status} 
                                size="small" 
                                color={getStatusColor(connection.status)}
                                sx={{ ml: 1 }}
                              />
                            </Box>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Typography variant="subtitle2">
                            {connection.connection_name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {connection.description}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={connection.db_type.toUpperCase()} size="small" />
                        </TableCell>
                        <TableCell>{connection.host}:{connection.port}</TableCell>
                        <TableCell>{connection.database}</TableCell>
                        <TableCell>
                          {connection.target_knowledge_bases.length > 0 ? (
                            connection.target_knowledge_bases.map(kb => (
                              <Chip key={kb} label={kb} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                            ))
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              미지정
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {connection.last_tested_at ? 
                            new Date(connection.last_tested_at).toLocaleString() : 
                            '미테스트'
                          }
                        </TableCell>
                        <TableCell>
                          <Tooltip title="연결 테스트">
                            <IconButton 
                              onClick={() => handleTestConnection(connection.connection_name)}
                              size="small"
                            >
                              <TestIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="스키마 발견">
                            <IconButton 
                              onClick={() => handleDiscoverSchema(connection.connection_name)}
                              size="small"
                            >
                              <RefreshIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="삭제">
                            <IconButton 
                              onClick={() => handleDeleteConnection(connection.connection_name)}
                              size="small"
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      {/* 스키마 설정 탭 */}
      <TabPanel value={tabValue} index={1}>
        <Alert severity="info" sx={{ mb: 2 }}>
          다음 단계에서 구현될 예정입니다.
        </Alert>
      </TabPanel>

      {/* 비즈니스 룰 탭 */}
      <TabPanel value={tabValue} index={2}>
        <Alert severity="info" sx={{ mb: 2 }}>
          다음 단계에서 구현될 예정입니다.
        </Alert>
      </TabPanel>

      {/* 테스트 탭 */}
      <TabPanel value={tabValue} index={3}>
        <Card>
          <CardHeader title="자연어 쿼리 테스트" />
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>테스트할 DB 연결</InputLabel>
                  <Select
                    value={selectedConnection || ''}
                    label="테스트할 DB 연결"
                    onChange={(e) => setSelectedConnection(e.target.value)}
                  >
                    {connections.filter(conn => conn.status === 'connected').map((connection) => (
                      <MenuItem key={connection.connection_name} value={connection.connection_name}>
                        {connection.connection_name} ({connection.db_type})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="자연어 질문"
                  placeholder="예: 상품 테이블에서 가격이 10000원 이상인 상품들을 보여줘"
                  value={testQuery}
                  onChange={(e) => setTestQuery(e.target.value)}
                  sx={{ mb: 2 }}
                />

                <Button
                  variant="contained"
                  onClick={handleTestQuery}
                  disabled={!selectedConnection || !testQuery || isLoading}
                  startIcon={isLoading ? <CircularProgress size={20} /> : <TestIcon />}
                >
                  쿼리 실행
                </Button>
              </Grid>

              <Grid item xs={12} md={6}>
                {queryResult && (
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      실행 결과
                    </Typography>
                    
                    {queryResult.success ? (
                      <Box>
                        <Alert severity="success" sx={{ mb: 2 }}>
                          성공적으로 실행되었습니다. ({queryResult.execution_time_ms?.toFixed(2)}ms, {queryResult.row_count}행)
                        </Alert>
                        
                        <Accordion>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography>생성된 SQL</Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <pre style={{ 
                              backgroundColor: '#f5f5f5', 
                              padding: '10px', 
                              borderRadius: '4px',
                              overflow: 'auto',
                              fontSize: '0.9rem'
                            }}>
                              {queryResult.generated_sql}
                            </pre>
                          </AccordionDetails>
                        </Accordion>

                        {queryResult.execution_result && queryResult.execution_result.length > 0 && (
                          <Accordion sx={{ mt: 1 }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Typography>조회 결과 ({queryResult.row_count}행)</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                              <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      {Object.keys(queryResult.execution_result[0]).map((column) => (
                                        <TableCell key={column}>{column}</TableCell>
                                      ))}
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {queryResult.execution_result.slice(0, 20).map((row, index) => (
                                      <TableRow key={index}>
                                        {Object.values(row).map((value, cellIndex) => (
                                          <TableCell key={cellIndex}>
                                            {value !== null ? String(value) : 'NULL'}
                                          </TableCell>
                                        ))}
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                              {queryResult.row_count > 20 && (
                                <Typography variant="caption" sx={{ mt: 1 }}>
                                  처음 20개 행만 표시됩니다.
                                </Typography>
                              )}
                            </AccordionDetails>
                          </Accordion>
                        )}
                      </Box>
                    ) : (
                      <Alert severity="error">
                        실행 실패: {queryResult.error_message}
                      </Alert>
                    )}
                  </Box>
                )}
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </TabPanel>

      {/* 연결 생성 대화상자 */}
      <Dialog 
        open={openConnectionDialog} 
        onClose={() => setOpenConnectionDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>새 데이터베이스 연결 추가</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="연결 이름"
                placeholder="예: 상품_DB"
                value={newConnection.connection_name}
                onChange={(e) => setNewConnection(prev => ({
                  ...prev,
                  connection_name: e.target.value
                }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>데이터베이스 타입</InputLabel>
                <Select
                  value={newConnection.db_type}
                  label="데이터베이스 타입"
                  onChange={(e) => setNewConnection(prev => ({
                    ...prev,
                    db_type: e.target.value,
                    port: e.target.value === 'mysql' ? 3306 : 
                          e.target.value === 'postgresql' ? 5432 : 1433
                  }))}
                >
                  <MenuItem value="mysql">MySQL</MenuItem>
                  <MenuItem value="postgresql">PostgreSQL</MenuItem>
                  <MenuItem value="mssql">SQL Server</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="호스트"
                placeholder="localhost 또는 IP 주소"
                value={newConnection.host}
                onChange={(e) => setNewConnection(prev => ({
                  ...prev,
                  host: e.target.value
                }))}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="포트"
                type="number"
                value={newConnection.port}
                onChange={(e) => setNewConnection(prev => ({
                  ...prev,
                  port: parseInt(e.target.value)
                }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="데이터베이스 이름"
                value={newConnection.database}
                onChange={(e) => setNewConnection(prev => ({
                  ...prev,
                  database: e.target.value
                }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="사용자명"
                value={newConnection.username}
                onChange={(e) => setNewConnection(prev => ({
                  ...prev,
                  username: e.target.value
                }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="비밀번호"
                type="password"
                value={newConnection.password}
                onChange={(e) => setNewConnection(prev => ({
                  ...prev,
                  password: e.target.value
                }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="설명"
                placeholder="이 연결에 대한 설명"
                value={newConnection.description}
                onChange={(e) => setNewConnection(prev => ({
                  ...prev,
                  description: e.target.value
                }))}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>연결할 지식베이스</InputLabel>
                <Select
                  multiple
                  value={newConnection.target_knowledge_bases}
                  label="연결할 지식베이스"
                  onChange={(e) => setNewConnection(prev => ({
                    ...prev,
                    target_knowledge_bases: e.target.value
                  }))}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))}
                    </Box>
                  )}
                >
                  {knowledgeBases.map((kb) => (
                    <MenuItem key={kb.kb_id} value={kb.kb_id}>
                      <Checkbox 
                        checked={newConnection.target_knowledge_bases.indexOf(kb.kb_id) > -1} 
                      />
                      {kb.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConnectionDialog(false)}>
            취소
          </Button>
          <Button 
            onClick={handleCreateConnection}
            variant="contained"
            disabled={!newConnection.connection_name || !newConnection.host || 
                     !newConnection.database || !newConnection.username || !newConnection.password}
          >
            생성
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DatabaseToolsPage; 